import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomerInfoService } from '../../services/customer-info.service';
import { AuthService } from '../../../services/auth.service';
import { TimesheetService } from '../../../services/timesheet.service';
import {
  CustomerInfoSearch,
  CustomerOfficeList,
  CustomerOfficeDetail,
  CustomerJobOrder,
  Approver,
  UpdateApproverEmail
} from '../../models/customer-info.model';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

@Component({
  selector: 'app-asc-customer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './asc-customer.html',
  styleUrls: ['./asc-customer.scss']
})
export class AscCustomerComponent implements OnInit {
  // Search criteria
  departmentCode = '';  // XXX-XXXX-XXX形式
  branchNumber = '';
  companyName = '';

  // Validation error messages
  departmentCodeError = '';
  branchNumberError = '';

  // Area display flags
  showOfficeList = false;
  showOfficeDetail = false;
  showJobOrders = false;
  showApprovers = false;

  // Data
  officeList: CustomerOfficeList[] = [];
  selectedOffice: CustomerOfficeDetail | null = null;
  jobOrders: CustomerJobOrder[] = [];
  approvers: Approver[] = [];

  // Pagination
  currentPage = 1;
  pageSize = 10;

  // Original email values for change tracking
  originalEmails: Map<number, string> = new Map();

  // Error and success messages
  errorMessage = '';
  successMessage = '';

  // 書き込み権限
  readWrite = false;
  adminLevel: number = 0;

  // 台帳一括ダウンロード
  yearMonthOptions: { value: string; label: string }[] = [];
  bulkYearMonth: string = '';
  bulkCustomerCode: string = '';
  isBulkDownloading: boolean = false;
  bulkErrorMessage: string = '';

  // Navigation events
  @Output() navigateToJobOrder = new EventEmitter<number>();
  @Output() navigateToAsc = new EventEmitter<{userCode?: string; ascUq?: string}>();

  constructor(
    private customerInfoService: CustomerInfoService,
    private authService: AuthService,
    private timesheetService: TimesheetService
  ) { }

  ngOnInit(): void {
    // ReadWriteを取得
    this.readWrite = this.authService.getReadWrite();
    this.adminLevel = this.authService.getAdminLevel() || 0;
    this.generateYearMonthOptions();
  }

  // 画面を初期状態に戻す
  onClear(): void {
    this.departmentCode = '';
    this.branchNumber = '';
    this.companyName = '';
    this.departmentCodeError = '';
    this.branchNumberError = '';
    this.showOfficeList = false;
    this.showOfficeDetail = false;
    this.showJobOrders = false;
    this.showApprovers = false;
    this.officeList = [];
    this.selectedOffice = null;
    this.jobOrders = [];
    this.approvers = [];
    this.currentPage = 1;
    this.originalEmails = new Map();
    this.errorMessage = '';
    this.successMessage = '';
    this.bulkYearMonth = '';
    this.bulkCustomerCode = '';
    this.isBulkDownloading = false;
    this.bulkErrorMessage = '';
  }

  // 部署コード入力処理（自動フォーマットなし・半角変換のみ）
  onDepartmentCodeInput(event: Event): void {
    if ((event as InputEvent).isComposing) return;
    const input = event.target as HTMLInputElement;
    this.departmentCode = this.toHalfWidth(input.value);
    this.departmentCodeError = '';

    // Pattern B をクリア
    if (this.departmentCode) {
      this.branchNumber = '';
      this.branchNumberError = '';
      this.companyName = '';
    }
  }

  // 部署コードの検証
  validateDepartmentCode(): void {
    if (this.departmentCode) {
      const withoutHyphens = this.departmentCode.replace(/-/g, '');
      // 完全な形式: 3桁数字 + 4桁（先頭は半角スペース可）+ 3桁数字 = ハイフン除去後10文字
      if (withoutHyphens.length !== 10) {
        this.departmentCodeError = '顧客部署番号は3桁-4桁-3桁の形式で入力してください。';
      } else if (!/^\d{3}-[ A-Za-z0-9][A-Za-z0-9]{3}-\d{3}$/.test(this.departmentCode)) {
        this.departmentCodeError = '顧客部署番号の形式が正しくありません。';
      } else {
        this.departmentCodeError = '';
      }
    } else {
      this.departmentCodeError = '';
    }
  }

  // Real-time validation for branch number (3-digit number)
  onBranchNumberInput(event: Event): void {
    if ((event as InputEvent).isComposing) return;
    const input = event.target as HTMLInputElement;
    // Convert to half-width and allow only numbers
    let value = this.toHalfWidth(input.value);
    value = value.replace(/[^0-9]/g, '');
    this.branchNumber = value;
    
    // Clear error when typing
    this.branchNumberError = '';
    
    // Clear Pattern A when Pattern B is used
    if (this.branchNumber) {
      this.departmentCode = '';
      this.departmentCodeError = '';
    }
  }

  // Validation on blur for branch number
  onBranchNumberBlur(): void {
    if (this.branchNumber && (this.branchNumber.length === 0 || this.branchNumber.length > 3)) {
      this.branchNumberError = '支店番号は1~3桁の数字で入力してください。';
    } else {
      this.branchNumberError = '';
    }
  }

  // Real-time validation for company name
  onCompanyNameInput(): void {
    // Clear Pattern A when Pattern B is used
    if (this.companyName) {
      this.departmentCode = '';
    }
  }

  // Check if search button can be enabled
  canSearch(): boolean {
    // Pattern A: 部署コードが入力されていれば有効（書式チェックはボタン押下時）
    const patternAValid = this.departmentCode.trim().length > 0;

    // Pattern B: Both branch number and company name must be filled
    const patternBValid = 
      this.branchNumber.length > 0 &&
      this.branchNumber.length <= 3 &&
      this.companyName.trim().length > 0;

    return patternAValid || patternBValid;
  }

  // Unified search method
  searchCustomerOffices(): void {
    this.errorMessage = '';
    this.successMessage = '';

    // Determine which pattern to use
    const usePatternA = this.departmentCode.trim().length > 0;

    if (usePatternA) {
      // 書式チェック: XXX-XXXX-XXX（5文字目＝中間セグメント先頭は半角スペースも許可）
      if (!/^\d{3}-[ A-Za-z0-9][A-Za-z0-9]{3}-\d{3}$/.test(this.departmentCode)) {
        this.departmentCodeError = '顧客部署番号は XXX-XXXX-XXX の形式で入力してください。';
        return;
      }
      this.departmentCodeError = '';

      // Pattern A: Search by department code
      this.customerInfoService.getCustomerOfficeDetailByCode(this.departmentCode).subscribe({
        next: (detail) => {
          this.selectedOffice = detail;
          this.bulkCustomerCode = detail.customerOfficeCode?.substring(0, 8) ?? '';
          this.showOfficeList = false;
          this.showOfficeDetail = true;
          this.loadRelatedData(detail.customerOfficeId);
        },
        error: (error) => {
          this.errorMessage = '該当する顧客情報が見つかりませんでした。';
          this.clearResults();
        }
      });
    } else {
      // Pattern B: Search by branch number and company name
      const branchId = parseInt(this.branchNumber);
      if (isNaN(branchId) || branchId <= 0 || branchId > 999) {
        this.errorMessage = '支店番号は1~999の数値で入力してください。';
        return;
      }

      const search: CustomerInfoSearch = {
        branchId: branchId,
        customerName: this.companyName
      };

      this.customerInfoService.searchCustomerOffices(search).subscribe({
        next: (offices) => {
          this.officeList = offices;
          this.currentPage = 1;
          this.showOfficeList = true;
          this.showOfficeDetail = false;
          this.showJobOrders = false;
          this.showApprovers = false;
          
          if (offices.length === 0) {
            this.errorMessage = '該当する顧客情報が見つかりませんでした。';
          }
        },
        error: (error) => {
          this.errorMessage = '検索中にエラーが発生しました。';
          this.clearResults();
        }
      });
    }
  }

  // Select office from list
  selectOffice(office: CustomerOfficeList): void {
    this.customerInfoService.getCustomerOfficeDetailById(office.customerOfficeId).subscribe({
      next: (detail) => {
        this.selectedOffice = detail;
        this.showOfficeList = false;
        this.showOfficeDetail = true;
        this.loadRelatedData(detail.customerOfficeId);
      },
      error: (error) => {
        this.errorMessage = '顧客情報の取得に失敗しました。';
      }
    });
  }

  // Load job orders and approvers registered to this customer
  loadRelatedData(customerOfficeId: number): void {
    // Always show areas 3, 4, 5 when loading detail
    this.showJobOrders = true;
    this.showApprovers = true;

    // Load job orders
    this.customerInfoService.getJobOrders(customerOfficeId).subscribe({
      next: (orders) => {
        this.jobOrders = orders;
      },
      error: (error) => {
        console.error('Error loading job orders:', error);
        this.jobOrders = [];
      }
    });

    // Load approvers registered to the selected customer
    this.customerInfoService.getApprovers(customerOfficeId).subscribe({
      next: (approvers) => {
        this.approvers = approvers;
        // Store original email values
        this.originalEmails.clear();
        approvers.forEach(a => {
          this.originalEmails.set(a.userId, a.email2 || a.email);
        });
      },
      error: (error) => {
        console.error('Error loading approvers:', error);
        this.approvers = [];
      }
    });
  }

  // Check if email has been modified
  isEmailModified(approver: Approver): boolean {
    const original = this.originalEmails.get(approver.userId);
    return original !== approver.email2;
  }

  // Validate email format
  isValidEmail(email: string): boolean {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Update approver email
  updateEmail(approver: Approver, inputElement: HTMLInputElement): void {
    // Clear previous messages
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.isValidEmail(approver.email2)) {
      this.errorMessage = '有効なメールアドレスを入力してください。';
      // Focus and select the input
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      }, 0);
      return;
    }

    // サーバー側で重複チェックを一元実施するため、事前確認を省いて直接更新を呼ぶ
    const data: UpdateApproverEmail = {
      userId: approver.userId,
      email2: approver.email2
    };

    this.customerInfoService.updateApproverEmail(data).subscribe({
      next: (result) => {
        if (result) {
          this.successMessage = 'メールアドレスを更新しました。';
          this.originalEmails.set(approver.userId, approver.email2);
          // Reload to get updated editDate
          if (this.selectedOffice) {
            this.loadRelatedData(this.selectedOffice.customerOfficeId);
          }
        } else {
          this.errorMessage = 'メールアドレスの更新に失敗しました。';
          setTimeout(() => {
            inputElement.focus();
            inputElement.select();
          }, 0);
        }
      },
      error: (error) => {
        // 重複メールアドレスはサーバーが 400 で返す
        if (error.error?.message === 'そのメールアドレスは既に使用されています') {
          alert(error.error.message);
        } else {
          this.errorMessage = 'メールアドレスの更新中にエラーが発生しました。';
        }
        setTimeout(() => {
          inputElement.focus();
          inputElement.select();
        }, 0);
      }
    });
  }

  // Reset approver email to original
  resetEmail(approver: Approver): void {
    // Clear previous messages
    this.errorMessage = '';
    this.successMessage = '';

    if (!confirm('登録メールアドレスを変更します。よろしいですか？')) {
      return;
    }

    this.customerInfoService.resetApproverEmail(approver.userId).subscribe({
      next: (result) => {
        if (result) {
          this.successMessage = 'メールアドレスをリセットしました。';
          // Reload to get updated data
          if (this.selectedOffice) {
            this.loadRelatedData(this.selectedOffice.customerOfficeId);
          }
        } else {
          this.errorMessage = 'メールアドレスのリセットに失敗しました。';
        }
      },
      error: (error) => {
        this.errorMessage = 'メールアドレスのリセット中にエラーが発生しました。';
      }
    });
  }

  // Reset user password
  resetPassword(approver: Approver): void {
    // Clear previous messages
    this.errorMessage = '';
    this.successMessage = '';

    if (!confirm(`${approver.userName}さんのパスワードをリセットします。よろしいですか?`)) {
      return;
    }

    this.customerInfoService.resetPassword(approver.userId).subscribe({
      next: (result) => {
        if (result) {
          this.successMessage = 'パスワードをリセットしました。';
          if (this.selectedOffice) {
            this.loadRelatedData(this.selectedOffice.customerOfficeId);
          }
        } else {
          this.errorMessage = 'パスワードのリセットに失敗しました。';
        }
      },
      error: (error) => {
        this.errorMessage = 'パスワードのリセット中にエラーが発生しました。';
      }
    });
  }

  // Clear all results
  clearResults(): void {
    this.officeList = [];
    this.selectedOffice = null;
    this.jobOrders = [];
    this.approvers = [];
    this.showOfficeList = false;
    this.showOfficeDetail = false;
    this.showJobOrders = false;
    this.showApprovers = false;
    this.originalEmails.clear();
  }

  // Pagination
  get pagedOfficeList(): CustomerOfficeList[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.officeList.slice(start, end);
  }

  get totalPages(): number {
    return Math.ceil(this.officeList.length / this.pageSize);
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  // Convert full-width characters to half-width
  private toHalfWidth(str: string): string {
    return str.replace(/[\uff10-\uff19]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
    }).replace(/[\uff21-\uff3a\uff41-\uff5a]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
    });
  }

  // Check if there are any validation errors
  hasValidationErrors(): boolean {
    return !!(this.departmentCodeError || this.branchNumberError);
  }

  // Navigation methods
  onJobOrderClick(jobOrderId: number): void {
    this.navigateToJobOrder.emit(jobOrderId);
  }

  onAscClick(userCode: string): void {
    this.navigateToAsc.emit({ userCode });
  }

  // Public method for external navigation
  loadByCustomerOfficeId(customerOfficeId: number): void {
    // 顧客部署IDから検索して概要と関連データを表示
    this.customerInfoService.getCustomerOfficeDetailById(customerOfficeId).subscribe({
      next: (detail) => {
        this.selectedOffice = detail;
        this.bulkCustomerCode = detail.customerOfficeCode?.substring(0, 8) ?? '';
        this.departmentCode = detail.customerOfficeCode ?? '';
        this.branchNumber = '';
        this.companyName = '';
        this.showOfficeList = false;
        this.showOfficeDetail = true;
        this.loadRelatedData(detail.customerOfficeId);
      },
      error: (error) => {
        this.errorMessage = '顧客情報の取得に失敗しました。';
        console.error('Error loading customer office detail:', error);
      }
    });
  }

  private generateYearMonthOptions(): void {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const startYear = currentYear - 3;
    this.yearMonthOptions = [];
    for (let year = currentYear; year >= startYear; year--) {
      const startMonth = year === currentYear ? currentMonth : 12;
      const endMonth = 1;
      for (let month = startMonth; month >= endMonth; month--) {
        const value = `${year}-${month.toString().padStart(2, '0')}`;
        const label = `${year}年${month}月`;
        this.yearMonthOptions.push({ value, label });
      }
    }
  }

  downloadBulkLedgerPdf(): void {
    if (!this.bulkYearMonth || !this.bulkCustomerCode) return;
    
    // MP社員（顧客コード「000- 000」）は一括出力不可
    if (this.bulkCustomerCode === '000- 000') {
      alert('[000- 000]の管理台帳は件数が多いため、一括出力できません。');
      return;
    }

    this.bulkErrorMessage = '';
    this.isBulkDownloading = true;

    this.timesheetService.getMPrepBulkPdf(this.bulkYearMonth, this.bulkCustomerCode).subscribe({
      next: (response: HttpResponse<Blob>) => {
        this.isBulkDownloading = false;
        const blob = response.body as Blob;
        const contentDisposition = response.headers.get('content-disposition') ?? '';
        const match = contentDisposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]+)\1/);
        const fileName = match ? match[2] : `ledger-bulk-${this.bulkYearMonth}-${this.bulkCustomerCode}.pdf`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error: HttpErrorResponse) => {
        this.isBulkDownloading = false;
        if (error.status === 404) {
          // 404 の場合は blob からエラーメッセージを取得
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const body = JSON.parse(reader.result as string);
              this.bulkErrorMessage = body.message ?? '対象のタイムシートが見つかりませんでした。';
            } catch {
              this.bulkErrorMessage = '対象のタイムシートが見つかりませんでした。';
            }
          };
          reader.readAsText(error.error);
        } else {
          this.bulkErrorMessage = 'PDF の生成中にエラーが発生しました。';
        }
      }
    });
  }
}
